import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Avatar, Button, Card, Col, Progress, Row, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd';
import {
  CheckCircleOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  FormOutlined,
  IdcardOutlined,
  ImportOutlined,
  InboxOutlined,
  MailOutlined,
  PlusOutlined,
  RightOutlined,
  SendOutlined,
  StopOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { DashboardOverview, DashboardPeriod, PeriodCount } from '@crm/shared';
import { useAuth } from '../auth/AuthProvider';
import { PageHeader } from '../components/PageHeader';
import { useDashboardOverview, useDashboardStats, type DashboardStats } from '../features/dashboard/api';
import { useFeatures, useSettings } from '../features/settings/api';
import { EmptyState, LoadError } from '../components/states';
import { CardTitle, StatCard, trendPct } from '../components/StatCard';
import { fmtDateTime, plural } from '../utils/format';
import { palette } from '../app/theme';

function useMoney() {
  const currency = useSettings().data?.defaultCurrency ?? 'USD';
  return (v: number, compact = false) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: compact || v >= 1000 ? 0 : 2, notation: compact ? 'compact' : 'standard' }).format(v);
}

const ViewAll = ({ to, label = 'View all' }: { to: string; label?: string }) => (
  <Link to={to}>
    <Button size="small" iconPlacement="end" icon={<RightOutlined style={{ fontSize: 10 }} />}>
      {label}
    </Button>
  </Link>
);

/** Contacts list opened with a filter already applied. */
const contactsWhere = (...conditions: object[]) => `/contacts?filter=${encodeURIComponent(JSON.stringify({ combinator: 'and', conditions }))}`;
const SUBSCRIBED = contactsWhere({ kind: 'field', field: 'subscribed', op: 'eq', value: true }, { kind: 'field', field: 'email', op: 'is_not_empty' });
const UNSUBSCRIBED = contactsWhere({ kind: 'field', field: 'subscribed', op: 'eq', value: false });

const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : '—');
const n = (v: number) => v.toLocaleString();

function PipelineCard({ s, loading }: { s?: DashboardStats; loading: boolean }) {
  const money = useMoney();
  const p = s?.pipeline;
  const open = p?.stages.filter((x) => x.kind === 'OPEN') ?? [];
  const won = p?.stages.filter((x) => x.kind === 'WON').reduce((a, x) => a + x.count, 0) ?? 0;
  const lost = p?.stages.filter((x) => x.kind === 'LOST').reduce((a, x) => a + x.count, 0) ?? 0;
  const totalValue = open.reduce((a, x) => a + x.value, 0);
  const totalCount = open.reduce((a, x) => a + x.count, 0);
  const share = (x: { value: number; count: number }) => (totalValue > 0 ? (x.value / totalValue) * 100 : totalCount > 0 ? (x.count / totalCount) * 100 : 0);
  return (
    <Card loading={loading} style={{ height: '100%' }} title={<CardTitle title="Pipeline" subtitle={p ? `Open deals by stage in ${p.name}` : 'Open deals by stage'} />} extra={<ViewAll to="/pipeline" label="View pipeline" />}>
      {!p || totalCount + won + lost === 0 ? (
        <EmptyState description="No deals yet. Add one to see your pipeline take shape." action={<Link to="/pipeline"><Button type="primary" icon={<PlusOutlined />}>New deal</Button></Link>} />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
            <span style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.02em' }}>{money(totalValue)}</span>
            <Typography.Text type="secondary">
              in {plural(totalCount, 'open deal')} · {won} won · {lost} lost
            </Typography.Text>
          </div>
          <Space orientation="vertical" size={14} style={{ width: '100%' }}>
            {open.map((st) => (
              <div key={st.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <Space size={8}>
                    <span style={{ width: 8, height: 8, borderRadius: 4, display: 'inline-block', background: st.color ?? palette.primary }} />
                    <Typography.Text strong>{st.name}</Typography.Text>
                    <Typography.Text type="secondary">{plural(st.count, 'deal')}</Typography.Text>
                  </Space>
                  <Typography.Text strong>{money(st.value)}</Typography.Text>
                </div>
                <Progress percent={share(st)} showInfo={false} strokeColor={st.color ?? palette.primary} size="small" style={{ margin: '4px 0 0' }} />
              </div>
            ))}
          </Space>
        </>
      )}
    </Card>
  );
}

function TopOrganizationsCard({ s, loading }: { s?: DashboardStats; loading: boolean }) {
  const money = useMoney();
  const orgs = s?.topOrganizations ?? [];
  return (
    <Card loading={loading} style={{ height: '100%' }} title={<CardTitle title="Top organizations" subtitle="By open deal value" />} extra={<ViewAll to="/organizations" />}>
      {orgs.length === 0 ? (
        <EmptyState description="Organizations with open deals will rank here." />
      ) : (
        <Space orientation="vertical" size={18} style={{ width: '100%' }}>
          {orgs.map((o) => (
            <Link key={o.id} to={`/organizations/${o.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'inherit' }}>
              <Avatar size={44} style={{ background: palette.primarySoft, color: palette.primary, fontWeight: 600, flex: 'none' }}>{o.name[0]?.toUpperCase()}</Avatar>
              <div style={{ minWidth: 0, flex: 1 }}>
                <Typography.Text strong ellipsis style={{ display: 'block' }}>{o.name}</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>{plural(o.deals, 'open deal')}</Typography.Text>
              </div>
              <Typography.Text strong style={{ flex: 'none' }}>{money(o.value)}</Typography.Text>
            </Link>
          ))}
        </Space>
      )}
    </Card>
  );
}

/** Who can be emailed: one bar split into ready, held back, bad addresses, no email and unsubscribed. */
function AudienceCard({ o, loading }: { o?: DashboardOverview; loading: boolean }) {
  const a = o?.audience;
  const parts = a
    ? [
        { key: 'ready', label: 'Ready to email', value: a.ready, color: palette.success, note: 'Subscribed, and their address is in a status campaigns send to' },
        { key: 'held', label: 'Held back', value: a.held, color: palette.warning, note: 'Subscribed, but their address status is switched off for campaigns (Verification → send settings)' },
        { key: 'bad', label: 'Bad address', value: a.bad, color: palette.error, note: 'Invalid, disposable or bounced addresses' },
        { key: 'noEmail', label: 'No email', value: a.subscribedNoEmail, color: '#C4C9D2', note: 'Subscribed contacts without any email address' },
        { key: 'unsub', label: 'Unsubscribed', value: a.unsubscribed, color: '#6B7280', note: 'Opted out: never emailed' },
      ]
    : [];
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <Card loading={loading} style={{ height: '100%' }} title={<CardTitle title="Who you can email" subtitle={a ? `${n(a.contacts)} contacts in the CRM` : undefined} />} extra={<ViewAll to="/tools/verification" label="Verification" />}>
      {a && (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <span style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.02em' }}>{n(a.ready)}</span>
            <Typography.Text type="secondary">ready to receive campaigns ({pct(a.ready, total)})</Typography.Text>
          </div>
          <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: palette.border, marginBottom: 16 }} role="img" aria-label="Contacts by email readiness">
            {parts.map((p) => (p.value > 0 ? <Tooltip key={p.key} title={`${p.label}: ${n(p.value)}`}><div style={{ width: `${(p.value / Math.max(total, 1)) * 100}%`, background: p.color, minWidth: 3 }} /></Tooltip> : null))}
          </div>
          <Space orientation="vertical" size={8} style={{ width: '100%' }}>
            {parts.map((p) => (
              <Tooltip key={p.key} title={p.note} placement="left">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <Space size={8}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, display: 'inline-block', background: p.color }} />
                    <Typography.Text>{p.label}</Typography.Text>
                  </Space>
                  <Typography.Text strong>
                    {n(p.value)} <Typography.Text type="secondary" style={{ fontWeight: 400 }}>· {pct(p.value, total)}</Typography.Text>
                  </Typography.Text>
                </div>
              </Tooltip>
            ))}
          </Space>
        </>
      )}
    </Card>
  );
}

function CampaignsCard({ o, loading }: { o?: DashboardOverview; loading: boolean }) {
  const c = o?.campaigns;
  return (
    <Card loading={loading} style={{ height: '100%' }} title={<CardTitle title="Campaigns" subtitle="Coming up, and how the latest ones did" />} extra={<ViewAll to="/campaigns" />}>
      {c && (
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Scheduled
            </Typography.Text>
            {c.scheduled.length ? (
              c.scheduled.map((s) => (
                <Link key={s.id} to={`/campaigns/${s.id}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: `1px solid ${palette.border}`, color: 'inherit' }}>
                  <Space size={8} style={{ minWidth: 0 }}>
                    <ClockCircleOutlined style={{ color: palette.primary }} />
                    <Typography.Text strong ellipsis style={{ maxWidth: 360 }}>{s.name}</Typography.Text>
                    {s.status === 'VERIFYING' && <Tag color="purple">Verifying</Tag>}
                  </Space>
                  <Typography.Text type="secondary" style={{ flex: 'none' }}>{s.scheduledAt ? fmtDateTime(s.scheduledAt) : 'When verified'}</Typography.Text>
                </Link>
              ))
            ) : (
              <Typography.Text type="secondary" style={{ display: 'block', padding: '8px 0' }}>
                Nothing scheduled.
              </Typography.Text>
            )}
          </div>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Recently sent
            </Typography.Text>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={c.recent}
              scroll={{ x: 560 }}
              locale={{ emptyText: 'No campaigns sent yet.' }}
              columns={[
                { title: 'Campaign', dataIndex: 'name', render: (v: string, r) => <Link to={`/campaigns/${r.id}`}>{v}</Link> },
                { title: 'Sent', dataIndex: 'sentCount', width: 80, align: 'right', render: (v: number) => n(v) },
                { title: 'Opened', key: 'o', width: 80, align: 'right', render: (_, r) => pct(r.openedCount, r.sentCount) },
                { title: 'Clicked', key: 'c', width: 80, align: 'right', render: (_, r) => pct(r.clickedCount, r.sentCount) },
                { title: 'Unsub.', dataIndex: 'unsubscribedCount', width: 80, align: 'right', render: (v: number) => n(v) },
                { title: 'Date', dataIndex: 'completedAt', width: 150, render: (v: string | null) => <Typography.Text type="secondary">{v ? fmtDateTime(v) : '—'}</Typography.Text> },
              ]}
            />
          </div>
        </Space>
      )}
    </Card>
  );
}

function SmallCard({ icon, title, to, children, loading }: { icon: ReactNode; title: string; to?: string; children: ReactNode; loading: boolean }) {
  return (
    <Card loading={loading} style={{ height: '100%' }} title={<Space>{icon}{title}</Space>} extra={to ? <ViewAll to={to} label="Open" /> : undefined}>
      {children}
    </Card>
  );
}

const vsText = (c: PeriodCount, period: DashboardPeriod) => `vs ${n(c.previous)} last ${period}`;

export function DashboardPage() {
  const { user } = useAuth();
  const features = useFeatures();
  const [period, setPeriod] = useState<DashboardPeriod>('week');
  const overview = useDashboardOverview(period);
  const o = overview.data;
  const loading = overview.isLoading;
  const pipelinesOn = features.enabled('pipelines');
  const showDeals = pipelinesOn && !!o?.hasDeals;
  const stats = useDashboardStats(showDeals);
  const firstName = user?.name?.split(' ')[0] ?? '';
  const label = period === 'week' ? 'this week' : 'this month';
  const err = overview.isError ? '—' : undefined;
  const sideCards = [
    features.enabled('inbox') && (
      <SmallCard key="inbox" icon={<InboxOutlined />} title="Inbox" to="/inbox" loading={loading}>
        <div style={{ fontSize: 26, fontWeight: 600 }}>{n(o?.inbox.unread ?? 0)}</div>
        <Typography.Text type="secondary">unread {o?.inbox.unread === 1 ? 'reply' : 'replies'}</Typography.Text>
        {o?.inbox.latest.map((t) => (
          <Link key={t.id} to="/inbox" style={{ display: 'block', marginTop: 8, color: 'inherit' }}>
            <Typography.Text strong ellipsis style={{ display: 'block' }}>{t.contact?.name ?? t.subject}</Typography.Text>
            <Typography.Text type="secondary" ellipsis style={{ display: 'block', fontSize: 12 }}>
              {t.subject} · {fmtDateTime(t.lastMessageAt)}
            </Typography.Text>
          </Link>
        ))}
      </SmallCard>
    ),
    features.enabled('cardScanner') && (
      <SmallCard key="cards" icon={<IdcardOutlined />} title="Scan cards" to="/tools/scan-cards" loading={loading}>
        <div style={{ fontSize: 26, fontWeight: 600 }}>{n(o?.cardScans.toCheck ?? 0)}</div>
        <Typography.Text type="secondary">{o?.cardScans.toCheck === 1 ? 'card' : 'cards'} waiting for you to check</Typography.Text>
        <div style={{ marginTop: 10 }}>
          {o?.cardScans.savedBy.length ? (
            o.cardScans.savedBy.map((s) => (
              <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text>{s.name}</Typography.Text>
                <Typography.Text strong>{n(s.count)} saved</Typography.Text>
              </div>
            ))
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>No cards saved {label}.</Typography.Text>
          )}
        </div>
      </SmallCard>
    ),
    features.enabled('forms') && (
      <SmallCard key="forms" icon={<FormOutlined />} title="Forms" to="/forms" loading={loading}>
        <div style={{ fontSize: 26, fontWeight: 600 }}>{n(o?.forms.current ?? 0)}</div>
        <Typography.Text type="secondary">
          submissions {label} ({vsText(o?.forms ?? { current: 0, previous: 0 }, period)})
        </Typography.Text>
        {o?.forms.topForm && (
          <Typography.Text style={{ display: 'block', marginTop: 10, fontSize: 13 }}>
            Most: <Link to={`/forms/${o.forms.topForm.id}`}>{o.forms.topForm.name}</Link> ({n(o.forms.topForm.count)})
          </Typography.Text>
        )}
      </SmallCard>
    ),
    features.enabled('tasks') && (
      <SmallCard key="tasks" icon={<CheckSquareOutlined />} title="Your tasks" to="/tasks/my" loading={loading}>
        <div style={{ fontSize: 26, fontWeight: 600 }}>{n(o?.tasksDue ?? 0)}</div>
        <Typography.Text type="secondary">{o?.tasksDue ? 'due today or overdue' : 'You are all caught up'}</Typography.Text>
      </SmallCard>
    ),
  ].filter(Boolean);

  return (
    <div className="page">
      <PageHeader
        title={`Welcome back${firstName ? `, ${firstName}` : ''}`}
        docTitle="Dashboard"
        subtitle={`Your audience, and what happened ${label} compared with last ${period}.`}
        extra={
          <Space wrap>
            <Segmented<DashboardPeriod>
              value={period}
              onChange={setPeriod}
              options={[
                { value: 'week', label: 'This week' },
                { value: 'month', label: 'This month' },
              ]}
            />
            <Link to="/tools/imports/new">
              <Button icon={<ImportOutlined />}>Import</Button>
            </Link>
          </Space>
        }
      />
      {overview.isError && (
        <div style={{ marginBottom: 24 }}>
          <LoadError error={overview.error} onRetry={overview.refetch} title="Could not load the dashboard" />
        </div>
      )}
      {features.enabled('campaigns') && <AttentionAlerts items={o?.attention ?? []} />}
      <Row gutter={[24, 24]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<CheckCircleOutlined />}
            label="Subscribed"
            value={err ?? n(o?.audience.subscribed ?? 0)}
            note={o ? `with an email address${o.audience.subscribedNoEmail ? ` (+${n(o.audience.subscribedNoEmail)} without)` : ''}` : undefined}
            loading={loading}
            to={SUBSCRIBED}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard icon={<StopOutlined />} label="Unsubscribed" value={err ?? n(o?.audience.unsubscribed ?? 0)} note="in total: never emailed" loading={loading} to={UNSUBSCRIBED} />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<UserDeleteOutlined />}
            label={`Unsubscribed ${label}`}
            value={err ?? n(o?.unsubscribes.current ?? 0)}
            trend={o ? { pct: trendPct(o.unsubscribes.current, o.unsubscribes.previous), text: vsText(o.unsubscribes, period), downIsGood: true } : undefined}
            loading={loading}
            to={UNSUBSCRIBED}
            detail={
              o && (o.unsubscribes.topCampaign || o.unsubscribes.viaImport > 0)
                ? [
                    o.unsubscribes.topCampaign && `Most from "${o.unsubscribes.topCampaign.name}" (${n(o.unsubscribes.topCampaign.count)}).`,
                    o.unsubscribes.viaImport > 0 && `Plus ${n(o.unsubscribes.viaImport)} imported already unsubscribed.`,
                  ]
                    .filter(Boolean)
                    .join(' ')
                : undefined
            }
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<UserAddOutlined />}
            label={`New contacts ${label}`}
            value={err ?? n(o?.newContacts.added ?? 0)}
            trend={o ? { pct: trendPct(o.newContacts.added, o.newContacts.previousAdded), text: `vs ${n(o.newContacts.previousAdded)} last ${period}` } : undefined}
            loading={loading}
            to="/contacts"
            detail={
              o && (o.newContacts.bySource.length > 0 || o.newContacts.imported > 0)
                ? [...o.newContacts.bySource.map((s) => `${s.source} ${n(s.count)}`), ...(o.newContacts.imported > 0 ? [`plus ${n(o.newContacts.imported)} imported`] : [])].join(' · ')
                : undefined
            }
          />
        </Col>

        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<SendOutlined />}
            label={`Campaign emails sent ${label}`}
            value={err ?? n(o?.emails.current ?? 0)}
            note={o?.emails.current ? `${pct(o.emails.opened, o.emails.current)} opened · ${pct(o.emails.clicked, o.emails.current)} clicked` : `None sent ${label}`}
            loading={loading}
            to="/campaigns"
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<WarningOutlined />}
            label={`Bounces & spam complaints ${label}`}
            value={err ?? `${n(o?.deliverability.bounces.current ?? 0)} / ${n(o?.deliverability.complaints.current ?? 0)}`}
            trend={
              o
                ? {
                    pct: trendPct(o.deliverability.bounces.current + o.deliverability.complaints.current, o.deliverability.bounces.previous + o.deliverability.complaints.previous),
                    text: `vs ${n(o.deliverability.bounces.previous)} / ${n(o.deliverability.complaints.previous)} last ${period}`,
                    downIsGood: true,
                  }
                : undefined
            }
            loading={loading}
            to="/tools/verification"
          />
        </Col>
        <Col xs={24} xl={12}>
          <AudienceCard o={o} loading={loading} />
        </Col>

        {features.enabled('campaigns') && (
          <Col xs={24} xl={sideCards.length ? 16 : 24}>
            <CampaignsCard o={o} loading={loading} />
          </Col>
        )}
        {sideCards.length > 0 && (
          <Col xs={24} xl={features.enabled('campaigns') ? 8 : 24}>
            <Row gutter={[24, 24]}>
              {sideCards.map((card) => (
                <Col key={(card as { key: string }).key} xs={24} sm={12} xl={features.enabled('campaigns') ? 24 : 6}>
                  {card}
                </Col>
              ))}
            </Row>
          </Col>
        )}

        {showDeals && (
          <>
            <Col xs={24} xl={16}>
              <PipelineCard s={stats.data} loading={stats.isLoading} />
            </Col>
            <Col xs={24} xl={8}>
              <TopOrganizationsCard s={stats.data} loading={stats.isLoading} />
            </Col>
          </>
        )}
      </Row>
      {!loading && !overview.isError && o?.audience.contacts === 0 && (
        <div style={{ marginTop: 24 }}>
          <EmptyState
            description="No contacts yet. Add or import contacts to get started."
            action={
              <Space>
                <Link to="/contacts"><Button type="primary" icon={<PlusOutlined />}>Add contacts</Button></Link>
                <Link to="/tools/imports/new"><Button icon={<MailOutlined />}>Import a file</Button></Link>
              </Space>
            }
          />
        </div>
      )}
    </div>
  );
}

/** Campaigns that need a look: a send cut off part-way, or delivery reports that never arrived. */
function AttentionAlerts({ items }: { items: DashboardOverview['attention'] }) {
  if (!items.length) return null;
  return (
    <Space orientation="vertical" size={12} style={{ width: '100%', marginBottom: 24 }}>
      {items.map((c) => (
        <Alert
          key={c.id}
          type="warning"
          showIcon
          title={
            c.problem === 'interrupted'
              ? `"${c.name}" stopped part-way: ${c.sentCount.toLocaleString()} of ${c.recipientCount.toLocaleString()} emails sent.`
              : `"${c.name}": delivery reports have not arrived, so its delivered, opened and clicked numbers are too low.`
          }
          description={
            c.problem === 'interrupted'
              ? 'Open it to resume sending to the rest, or leave it stopped.'
              : c.syncing
                ? 'A sync from Resend is fetching the missing numbers; this goes away when it is done.'
                : 'Open it and press "Sync from Resend" to fetch the missing numbers.'
          }
          action={
            <Link to={`/campaigns/${c.id}`}>
              <Button size="small">Open campaign</Button>
            </Link>
          }
        />
      ))}
    </Space>
  );
}
